import { AssessmentResult, AssessmentSettings } from "@shared/models";
import { EChartsOption } from "echarts";

const legendParams = [
  { name: 'Learner Score', color: '#247e8d' },
  { name: 'Group median', color: '#aaaaaa' },
];

export function getAssessmentDisplayParams(assessmentResult: AssessmentResult, assessmentSettings: AssessmentSettings) {
  const criterias = assessmentResult.report.criterias.filter(c => !isNaN(c.grade));
  const scoreMax = assessmentSettings.maxScore || 5;
  const scoreMin = assessmentSettings.minScore || 0;
  const total = criterias.reduce((acc, c) => acc + c.grade, 0);
  const score = Math.max(scoreMin, total / criterias.length).toFixed(1);
  
  const userName = getUserName(assessmentResult);
  const initials = userName
    .split(/\s+/)
    .map(s => s.length ? s[0].toUpperCase() : '')
    .join('')
    .substring(0, 2);

  return { scoreMax, scoreMin, score, userName, initials };
}

export function setupAssessmentChart(
  assessmentResult: AssessmentResult,
  maxScore: number,
  compactMode = false
): EChartsOption {
  const criterias = assessmentResult.report.criterias;
  criterias.forEach((c, index) => c.index = index);

  let chartColors = [legendParams[0].color];
  let chartData: any[] = [{
    value: criterias.map(c => c.grade),
    name: legendParams[0].name
  }];

  if (!compactMode) {
    chartColors.unshift(legendParams[1].color);
    chartData.unshift({
      value: criterias.map(c => c.medianGrade),
      name: legendParams[1].name,
      lineStyle: {
        type: 'dashed'
      }
    });
  }

  const targetSplitNumber = 5;
  const splitInterval = Math.ceil(maxScore / targetSplitNumber);
  const splitNumber = Math.ceil(maxScore / splitInterval);
  const displayedMaxScore = splitInterval * splitNumber;

  return {
    legend: compactMode ? undefined : {
      data: legendParams.map(p => p.name),
      orient: 'vertical',
      right: 0,
      bottom: 0,
    },
    radar: {
      shape: 'circle',
      axisName: {
        formatter: '{value}',
        color: '#aaa',
        fontSize: compactMode ? 9 : 14,
        fontWeight: 'lighter',
        overflow: 'truncate'
      },
      splitArea: {
        areaStyle: {
          color: ['rgba(0,0,0,0)'],
        }
      },
      indicator: criterias.map(c => {
        return {
          name: compactMode ? `${c.index + 1}` : `${c.index + 1}. ${c.title}`,
          max: displayedMaxScore
        }
      }),
      splitNumber: splitNumber
    },
    series: [
      {
        type: 'radar',
        color: chartColors,
        data: chartData
      }
    ]
  };
}

function getUserName(assessmentResult: AssessmentResult) {
  const user = assessmentResult.user;
  let parts = [];
  if (user?.first_name) {
    parts.push(user?.first_name);
  }
  if (user?.last_name) {
    parts.push(user?.last_name);
  }
  if (parts.length > 0) {
    return parts.join(' ');
  }
  // Fallback to actor_name
  return assessmentResult.session?.actor_name || 'Anonymous';
}